import { createContext, useContext, useMemo, type ReactNode } from "react";

import { mergeBell, type BellItem, type BellTarget, type LocalBell } from "./bellRows";
import type { InboxParamFormatter } from "./inboxParams";
import { useInbox, type UseInboxOptions } from "./useInbox";

// Single-fetch seam (INBOX-1, INBOX-10). Same shape CN-coach-edges-fetch
// already fixed for the coach roster (apps/mobile/src/coach/CoachEdgesContext.tsx):
// a context mounted ONCE near the shell root owns the one `useInbox` call,
// and every consumer below it (the bell, its panel, InboxSection on Home,
// the bell inside the logger's Modal) reads that SAME state, never a
// second GET. Platform-neutral: apiFetch is injected, so both mobile's
// TabShell and web-rnw's AppShell mount this with their own fetcher.
// No `audience` prop since INBOX-10: one read carries every role.
// #294: the phone's own rows join the server's in `items` and `count`; web
// passes no `local`. #182: `formatParams` is each host's way to make a row's
// params readable (useInboxParamFormatter).
export type InboxContextValue = Omit<ReturnType<typeof useInbox>, "items"> & {
  items: BellItem[] | null;
  local?: LocalBell;
  formatParams?: InboxParamFormatter;
  /** The host's way to a target, for a server row that leads somewhere
   *  (#296: the missing bodyweight opens Me → Measurements). False when it
   *  could not navigate; absent, such a row offers no "open". */
  openTarget?: (target: BellTarget) => boolean;
};

// The "say nothing" shape InboxSection already treats status "idle" as,
// used only when a screen renders outside a provider (should not happen
// in the two wired hosts, but a silent default beats a crash).
const defaultValue: InboxContextValue = {
  items: null,
  count: 0,
  status: "idle",
  error: null,
  hasMore: false,
  refresh: async () => {},
  loadMore: async () => {},
  busyId: null,
  actionError: null,
  respondToProposal: async () => null,
  dismissNotice: async () => null,
};

const InboxContext = createContext<InboxContextValue>(defaultValue);

export type InboxProviderProps = UseInboxOptions & {
  local?: LocalBell;
  formatParams?: InboxParamFormatter;
  openTarget?: (target: BellTarget) => boolean;
  children: ReactNode;
};

export function InboxProvider({
  apiFetch,
  onMaxWritten,
  local,
  formatParams,
  openTarget,
  children,
}: InboxProviderProps) {
  const server = useInbox({ apiFetch, onMaxWritten });
  const value = useMemo<InboxContextValue>(() => {
    const merged = local
      ? { ...server, ...mergeBell(server.items, server.count, local.rows), local }
      : server;
    return {
      ...merged,
      ...(formatParams ? { formatParams } : {}),
      ...(openTarget ? { openTarget } : {}),
    };
  }, [server, local, formatParams, openTarget]);
  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
}

export function useInboxContext(): InboxContextValue {
  return useContext(InboxContext);
}
